/**
 * Edit Service Route
 *
 * Form for editing existing services
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useService } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { ServiceForm } from '../../components/services';
import { updateService, ApiError, type UpdateServiceInput } from '../../lib/apiClient';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import type { CreateServiceFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './edit.css';

export const Route = createFileRoute('/services/$serviceId/edit')({
  component: EditServicePage,
});

function EditServicePage(): JSX.Element {
  const { serviceId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch service data
  const { data, loading, error: fetchError } = useService(serviceId);
  const service = data?.service;

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateServiceFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Convert form data to API input. `editServiceSchema` allows teamId to
      // be '' (the sentinel `defaultValues.teamId = service.teamId ?? ''`
      // uses for an unowned service, since the field isn't required in edit
      // mode) but the backend's `teamId` schema only accepts a real
      // `team-{name}` string or `null` -- an empty string fails its regex
      // and 400s, which silently stranded the form on /edit with no
      // navigation (issue #1195). Normalize '' to null so clearing/leaving a
      // service unowned round-trips correctly.
      const input: UpdateServiceInput = {
        name: data.name,
        shortName: data.shortName,
        description: data.description,
        serviceType: data.serviceType,
        teamId: data.teamId || null,
        repo: data.repo || undefined,
        lifecycleStatus: data.lifecycleStatus,
        tier: data.tier,
      };

      // Update service via REST API
      await updateService(serviceId, input);

      // Navigate back to service detail page immediately -- the destination
      // page's own useService query fetches current data independently, so
      // navigation must not wait on an unrelated ListServices cache refresh.
      // Awaiting that refetch here occasionally took long enough under CI
      // load to exceed a 10s test timeout, blocking navigation for no
      // functional reason (frontend/e2e/services-mutations.spec.ts:248).
      navigate({ to: `/services/${serviceId}` });

      // Refresh caches in the background so a later visit to the list or
      // detail page sees current data; failure here doesn't affect the user,
      // who has already moved on.
      void apolloClient
        .refetchQueries({ include: ['GetService', 'ListServices'] })
        .catch(() => undefined);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/services/${serviceId}` });
  };

  // Loading state
  if (loading) {
    return (
      <div className="edit-service-page">
        <LoadingSpinner centered message="Loading service..." />
      </div>
    );
  }

  // Error state
  if (fetchError || !service) {
    return (
      <div className="edit-service-page">
        <EmptyState
          icon="error"
          title="Failed to load service"
          description={fetchError?.message || 'Service not found'}
          actionLabel="Back to Services"
          onAction={() =>
            navigate({
              to: '/services',
              search: {
                page: 1,
                pageSize: 25,
                search: '',
                lifecycleStatus: '',
                tier: '',
                tagId: '',
                teamId: '',
                technologyId: '',
              },
            })
          }
        />
      </div>
    );
  }

  // Prepare default values from service data
  const defaultValues: Partial<CreateServiceFormData> = {
    name: service.name,
    shortName: service.shortName || undefined,
    description: service.description || undefined,
    serviceType: service.serviceType,
    // Unowned services have a null teamId. '' (not undefined) so
    // editServiceSchema's teamId validation sees the empty case it
    // explicitly allows, rather than an unregistered/undefined field.
    teamId: service.teamId ?? '',
    repo: service.repo || undefined,
    lifecycleStatus: service.lifecycleStatus || 'development',
    tier: service.tier,
  };

  return (
    <div className="edit-service-page">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <Link
          to="/services"
          search={{
            page: 1,
            pageSize: 25,
            search: '',
            lifecycleStatus: '',
            tier: '',
            tagId: '',
            teamId: '',
            technologyId: '',
          }}
          className="breadcrumb-link"
        >
          <span>Services</span>
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/services/$serviceId" params={{ serviceId }} className="breadcrumb-link">
          <span>{service.name}</span>
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Edit</span>
      </div>

      {/* Header */}
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Service</h1>
        <p className="forge-typography--body2 page-header-subtitle">Update service information</p>
      </div>

      {/* Error Banner */}
      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      {/* Service Form */}
      <ServiceForm
        key={serviceId}
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

// Export for testing
export { EditServicePage };
